/**
 * Eingabe der E-Rechnung aus einem festgeschriebenen Beleg: dieselbe Umwandlung für das Portal (Herunterladen, Prüfung vor dem
 * Versand) und den Mailversand (XML als Anhang). Grundlage sind die beim Festschreiben eingefrorenen Angaben (0055:
 * issuer_snapshot mit Bank und Steuerhinweis, recipient_snapshot mit Referenz), also dieselben Werte wie auf dem archivierten PDF.
 * Heute gelesen werden nur Telefon, Ansprechperson und deren E-Mail der Agentur (Kontakt, BR-DE-5 … 7).
 *
 * Belegart: Stornorechnung 381 (mit Bezug auf die Originalrechnung), Gutschrift 389, sonst Rechnung 380.
 * Gutschrift an den Creator (K-417c): Verkäufer ist der Creator (issuer_snapshot = app.talent_party), Käufer die Agentur
 * (recipient_snapshot = app.org_party); Kontakt des Verkäufers ist dann der Creator (creatorContactOf), nicht die Agentur.
 * Käuferreferenz (BT-10): Referenz aus dem Empfänger-Schnappschuss (Bestellnummer der Kooperation bzw. Käuferreferenz oder
 * Leitweg-ID der Marke, 0066), sonst die Rechnungsnummer.
 */
import type { Cents, Currency } from "./types.js";
import { ownInvoiceRevenueAll, type OwnInvoiceRow } from "./own-revenue.js";
import { checkVatIdFormat, isEuVatIdFormat, taxModeFromDb } from "./tax.js";
import { isEuCountry, normalizeCountry } from "./countries.js";
import { buildEInvoiceXml, checkEInvoice, eInvoiceFileName, type EInvoiceInput, type EInvoiceParty, type EInvoiceProblem } from "./e-invoice.js";

type Snap = Record<string, unknown> | null | undefined;

/** Rechnungszeile, wie Portal (PostgREST) und Server (SQL) sie lesen; Zahlen dürfen als Text kommen (numeric, bigint). */
export interface EInvoiceSourceInvoice {
  number?: string | null;
  issue_date?: unknown;
  currency?: string | null;
  self_billed?: boolean | null;
  service_period_start?: unknown;
  service_period_end?: unknown;
  effective_due_date?: unknown;
  document_due_date?: unknown;
  net_cents?: number | string | null;
  tax_cents?: number | string | null;
  gross_cents?: number | string | null;
  paid_cents?: number | string | null;
  tax_mode?: string | null;
  payment_term_days?: number | null;
  /** K-309: Umsatzsteuer in Euro bei fremder Währung (0267/0269), eingefroren wie auf dem PDF */
  tax_eur_cents?: number | string | null;
  /** Sprache des Belegs (Klartexte im Datensatz, etwa die Zahlungsanweisung ohne IBAN) */
  document_language?: string | null;
  issuer_snapshot?: Snap;
  recipient_snapshot?: Snap;
}

export interface EInvoiceSourceLine {
  description: string;
  quantity?: number | string | null;
  unit_cents: number | string;
  net_cents: number | string;
  tax_rate_bp?: number | string | null;
}

/** Kontakt der Agentur von heute (org_legal: Telefon, Vertretung; org_settings: Antwortadresse) */
export interface EInvoiceSellerContactSource {
  phone: string | null;
  contactName: string | null;
  contactEmail: string | null;
}

export interface EInvoiceSource {
  invoice: EInvoiceSourceInvoice;
  lines: readonly EInvoiceSourceLine[];
  /** Originalrechnung, wenn der Beleg eine Stornorechnung ist */
  original?: { number: string | null; issue_date?: unknown } | null;
  seller: EInvoiceSellerContactSource;
  /**
   * Adresse, an die die Rechnung geht (Empfänger der Mail): elektronische Adresse des Empfängers (BT-49), wenn der
   * Schnappschuss keine Rechnungs-E-Mail hat.
   */
  buyerEmail?: string | null;
}

const str = (v: unknown): string | null => (typeof v === "string" && v.trim() ? v.trim() : null);
const iso = (v: unknown): string | null => {
  if (v instanceof Date) return Number.isNaN(v.getTime()) ? null : v.toISOString().slice(0, 10);
  const s = str(v);
  return s && /^\d{4}-\d{2}-\d{2}/.test(s) ? s.slice(0, 10) : null;
};
const num = (v: unknown): number => {
  const n = Number(v ?? 0);
  return Number.isFinite(n) ? n : 0;
};

function partyOf(s: Snap): EInvoiceParty {
  const x = s ?? {};
  return {
    name: str(x["name"]) ?? "", street: str(x["street"]), postalCode: str(x["postal_code"]), city: str(x["city"]),
    country: str(x["country"]) ?? "", vatId: str(x["vat_id"]), taxNumber: str(x["tax_number"]), registration: str(x["register"]), email: str(x["email"]),
  };
}

/**
 * K-434: USt-IdNr. des Verkäufers (Creator in der Gutschrift, Agentur in der Rechnung an die Marke) kommt ungeprüft aus dem
 * festgeschriebenen Stand; beide Profile speichern eine falsche Form nur mit Hinweis (checkVatIdFormat sperrt nie). Ungültig und
 * eine Steuernummer vorhanden: im Datensatz nur die Steuernummer (BT-32 reicht für BR-DE-16/BR-S-02). Ohne Steuernummer bleibt sie
 * stehen und checkEInvoice meldet „USt-IdNr. … ungültig“, statt eine falsche Kennung zu verschicken.
 */
function sellerVatChecked(p: EInvoiceParty): EInvoiceParty {
  // 0444 (Steuerprüfung L7): Verkäufer außerhalb der EU (ohne CH/LI): die Steuernummer des Herkunftslandes (z. B. TRN) ist keine
  // USt-IdNr. und steht als Steuernummer (BT-32, schemeID FC) im Datensatz, nie als USt-IdNr. (BT-31, VA)
  const c = normalizeCountry(p.country || null);
  if (p.vatId && c && !isEuCountry(c) && c !== "CH" && c !== "LI" && !isEuVatIdFormat(p.vatId)) return { ...p, vatId: null, taxNumber: p.taxNumber || p.vatId };
  if (!p.vatId || !p.taxNumber) return p;
  return checkVatIdFormat(p.vatId, p.country || null) ? { ...p, vatId: null } : p;
}

/** Eingabe für checkEInvoice/buildEInvoiceXml (Profil XRechnung) aus Beleg, Positionen, Original und Kontakt der Agentur. */
export function eInvoiceInputFrom(src: EInvoiceSource): EInvoiceInput {
  const i = src.invoice;
  const issuer = i.issuer_snapshot ?? null;
  const recipient = i.recipient_snapshot ?? null;
  const bank = (issuer?.["bank"] ?? null) as Snap;
  const typeCode = src.original ? 381 : i.self_billed === true ? 389 : 380;
  const start = iso(i.service_period_start);
  const end = iso(i.service_period_end);
  const number = String(i.number ?? "");
  const sellerParty = sellerVatChecked(partyOf(issuer));
  const contactEmail = str(src.seller.contactEmail);
  const buyer = partyOf(recipient);
  const reference = str(recipient?.["reference"]);
  const selfBilling = i.self_billed === true;
  return {
    profile: "xrechnung",
    typeCode,
    selfBilling,
    // K-417c: Pflichtwort „Gutschrift“ (§ 14 Abs. 4 Satz 1 Nr. 10 UStG) auch im Datensatz (BT-22), wie auf dem PDF
    ...(selfBilling ? { notes: [src.original ? `Gutschrift: Storno der Gutschrift ${String(src.original.number ?? "").trim()}`.trim() : "Gutschrift"] } : {}),
    number,
    issueDate: iso(i.issue_date) ?? "",
    dueDate: typeCode === 381 ? null : iso(i.effective_due_date) ?? iso(i.document_due_date),
    currency: String(i.currency ?? "EUR") as Currency,
    servicePeriod: start && end ? { from: start, to: end } : null,
    deliveryDate: !start && end ? end : null,
    // Käuferreferenz (BT-10): Bestellnummer/Leitweg-ID der Marke, sonst die Rechnungsnummer
    buyerReference: reference ?? number,
    // BT-13 nur für echte Bestellnummern (Kooperation); ältere Schnappschüsse ohne Art kannten nur die Kooperationsreferenz
    orderReference: recipient?.["reference_kind"] === "buyer" ? null : reference,
    precedingInvoice: src.original ? { number: src.original.number ?? "", date: iso(src.original.issue_date) } : null,
    // Elektronische Adresse des Ausstellers (BT-34): eingefroren, sonst die Adresse der Ansprechperson von heute
    seller: { ...sellerParty, email: sellerParty.email ?? contactEmail },
    sellerContact: { name: str(src.seller.contactName) ?? str(issuer?.["representative"]), phone: str(src.seller.phone), email: contactEmail ?? sellerParty.email },
    buyer: { ...buyer, email: buyer.email ?? str(src.buyerEmail) },
    lines: src.lines.map((l) => ({
      name: l.description, quantity: l.quantity == null || l.quantity === "" ? 1 : num(l.quantity), netPriceCents: num(l.unit_cents), netAmountCents: num(l.net_cents),
      ratePercent: num(l.tax_rate_bp) / 100,
    })),
    taxMode: str(i.tax_mode),
    taxNotice: str(issuer?.["notice"]),
    totals: { netCents: num(i.net_cents), taxCents: num(i.tax_cents), grossCents: num(i.gross_cents), prepaidCents: num(i.paid_cents) || undefined },
    // K-309: Steuerwährung EUR (BT-6/BT-111), wenn die Umsatzsteuer in Euro eingefroren ist
    taxCurrency: String(i.currency ?? "EUR") !== "EUR" && i.tax_eur_cents != null && i.tax_eur_cents !== "" && num(i.tax_cents) !== 0
      ? { code: "EUR" as Currency, taxCents: num(i.tax_eur_cents) } : null,
    // Zahlungsweg (BG-16/17): IBAN, in der Gutschrift ohne IBAN auch PayPal/Wise bzw. Kontonummer (paymentMeansOf, 16.10.2026)
    payment: bank ? { iban: str(bank["iban"]), bic: str(bank["bic"]), accountName: str(bank["holder"]), method: str(bank["method"]), account: str(bank["account"]), email: str(bank["email"]) } : null,
    document: { language: str(i.document_language) },
    paymentTerms: typeof i.payment_term_days === "number" ? `Zahlbar innerhalb von ${i.payment_term_days} Tagen nach Zugang ohne Abzug` : null,
  };
}

/** Prüfen und, wenn vollständig, das XML mit Dateinamen erzeugen (Portal und Versand). */
export function eInvoiceFrom(src: EInvoiceSource): { ok: true; xml: string; filename: string; input: EInvoiceInput } | { ok: false; problems: EInvoiceProblem[]; input: EInvoiceInput } {
  const input = eInvoiceInputFrom(src);
  const problems = checkEInvoice(input);
  if (problems.length) return { ok: false, problems, input };
  return { ok: true, xml: buildEInvoiceXml(input), filename: eInvoiceFileName(input.number, input.profile), input };
}

/**
 * Soll die Rechnung an diese Marke zusätzlich als E-Rechnung gehen (brands.e_invoice, 0066)? Ja, wenn die Marke es wünscht oder,
 * wenn ihre Rechnungen über ihre Agentur laufen, die Agentur (sie ist dann der Rechnungsempfänger). Gleiche Regel im Versand.
 */
export function eInvoiceWanted(
  brand: { e_invoice?: boolean | null; invoice_via_agency?: boolean | null; represented_by?: string | null } | null | undefined,
  agency?: { e_invoice?: boolean | null } | null,
): boolean {
  if (!brand) return false;
  return brand.e_invoice === true || (brand.invoice_via_agency === true && !!brand.represented_by && agency?.e_invoice === true);
}

/**
 * Steuerprüfung 23.10.2026, L11: Vorgabe E-Rechnung für Rechnungen der Agentur an Marken. Seit 2025 ist bei Inlandsumsätzen
 * zwischen Unternehmern die E-Rechnung Pflicht; die sonstige Rechnung (PDF) bleibt nur bis 31.12.2026 zulässig, für 2027 nur bei
 * Vorjahresumsatz bis 800.000 € (§ 14 Abs. 1 und 2 UStG, § 27 Abs. 38 UStG). Ausgenommen sind Kleinbetragsrechnungen bis 250 €
 * brutto (§ 33 Satz 4 UStDV) und Kleinunternehmer (§ 34a Satz 3 UStDV). Gleiche Entscheidungslogik wie selfBillingEInvoiceDecision:
 * Agentur (Ausstellerin) und Marke (Empfängerin) in Deutschland, Marke Unternehmerin, Agentur kein Kleinunternehmer, keine
 * Kleinbetragsrechnung, maßgeblich der spätere Tag von Rechnungsdatum und Leistungsende. Stichtag: der Tag „E-Rechnung ab“ der
 * Agentur (org_settings.self_billing_e_invoice_from, er beruht auf ihrem Umsatz), ohne Angabe vorsichtig der 1.1.2027, spätestens
 * immer der 1.1.2028. Der Schalter an der Marke (brands.e_invoice, bei Abrechnung über ihre Agentur deren Schalter) übersteuert
 * die Vorgabe nur nach oben: an heißt immer E-Rechnung, aus heißt Vorgabe.
 */
export const BRAND_E_INVOICE_DEFAULT_FROM = "2027-01-01";
/** Ab hier gilt die Pflicht für alle Unternehmer (Ende der Übergangszeit, § 27 Abs. 38 UStG) */
export const BRAND_E_INVOICE_LATEST_FROM = "2028-01-01";
/** Kleinbetragsrechnung (§ 33 UStDV): bis 250 € brutto keine E-Rechnungspflicht */
export const BRAND_E_INVOICE_SMALL_AMOUNT_CENTS = 25_000;

export type BrandEInvoiceReason =
  /** Marke (bzw. ihre abrechnende Agentur) wünscht E-Rechnungen */
  | "brand_on"
  /** Vorgabe: Inlandsumsatz zwischen Unternehmern ab Stichtag */
  | "auto_on"
  | "before_cutoff"
  | "agency_abroad"
  | "brand_abroad"
  | "brand_private"
  | "small_business"
  | "small_amount";

export interface BrandEInvoiceFacts {
  /** Ergebnis von eInvoiceWanted (Schalter an der Marke bzw. ihrer abrechnenden Agentur) */
  brandChoice?: boolean | null;
  /** org_settings.self_billing_e_invoice_from (Stichtag aus dem Umsatz der Agentur); leer = BRAND_E_INVOICE_DEFAULT_FROM */
  agencyFrom?: unknown;
  /** Land der Agentur (issuer_snapshot) und des Empfängers (recipient_snapshot) */
  agencyCountry?: string | null;
  recipientCountry?: string | null;
  /** recipient_snapshot.business: false bzw. "false" = Privatperson; sonst Unternehmer */
  recipientBusiness?: unknown;
  taxMode?: string | null;
  /** issuer_snapshot.small_business */
  smallBusiness?: boolean | null;
  grossCents?: number | null;
  currency?: string | null;
  issueDate?: unknown;
  serviceEnd?: unknown;
}

export function brandEInvoiceDecision(f: BrandEInvoiceFacts): { wanted: boolean; reason: BrandEInvoiceReason; from: string } {
  const own = iso(f.agencyFrom);
  const from = own && own < BRAND_E_INVOICE_LATEST_FROM ? own : own ? BRAND_E_INVOICE_LATEST_FROM : BRAND_E_INVOICE_DEFAULT_FROM;
  if (f.brandChoice === true) return { wanted: true, reason: "brand_on", from };
  const land = (v: unknown) => (str(v) ?? "").toUpperCase();
  if (land(f.agencyCountry) !== "DE") return { wanted: false, reason: "agency_abroad", from };
  if (land(f.recipientCountry) !== "DE") return { wanted: false, reason: "brand_abroad", from };
  if (f.recipientBusiness === false || f.recipientBusiness === "false") return { wanted: false, reason: "brand_private", from };
  if (f.smallBusiness === true || taxModeFromDb(f.taxMode) === "SMALL_BUSINESS") return { wanted: false, reason: "small_business", from };
  // Kleinbetragsrechnung nur in Euro; fremde Währung zählt vorsichtig nie als Kleinbetrag
  if ((str(f.currency) ?? "EUR").toUpperCase() === "EUR" && Math.abs(Number(f.grossCents ?? 0)) <= BRAND_E_INVOICE_SMALL_AMOUNT_CENTS) {
    return { wanted: false, reason: "small_amount", from };
  }
  const ref = [iso(f.issueDate), iso(f.serviceEnd)].filter((d): d is string => !!d).sort().pop() ?? null;
  if (!ref || ref < from) return { wanted: false, reason: "before_cutoff", from };
  return { wanted: true, reason: "auto_on", from };
}

/** Klartext für den Fehler im Postausgang (last_error) bzw. Dialog: was fehlt, ohne Regelkennungen */
export function eInvoiceProblemText(problems: readonly EInvoiceProblem[]): string {
  return `E-Rechnung unvollständig: ${[...new Set(problems.map((p) => p.text))].join(" ")}`;
}

// ── K-417c: E-Rechnung zur Gutschrift an den Creator (Gutschriftverfahren, § 14 Abs. 2 Satz 5 UStG) ─────────────────────

/**
 * Kontakt des Verkäufers in der Gutschrift (BR-DE-5 … 7). Verkäufer ist der leistende Creator, also auch die Ansprechperson:
 * bei eigener Firma die eingefrorene Vertretung, sonst der Name; Telefon von heute (Creator-Profil, WhatsApp-Nummer; eine
 * eigene Telefonspalte gibt es nicht); E-Mail eingefroren, sonst die von heute.
 */
export function creatorContactOf(issuer: Snap, today: { phone?: string | null; email?: string | null } | null | undefined): EInvoiceSellerContactSource {
  return {
    contactName: str(issuer?.["representative"]) ?? str(issuer?.["name"]),
    phone: str(today?.phone),
    contactEmail: str(issuer?.["email"]) ?? str(today?.email),
  };
}

/**
 * Ab wann Gutschriften an Creator nach der Vorgabe als E-Rechnung gehen, wenn die Agentur keinen eigenen Stichtag eingetragen
 * hat (org_settings.self_billing_e_invoice_from, 0335).
 *
 * Entscheidung (16.10.2026): Die Pflicht, zwischen inländischen Unternehmern eine E-Rechnung auszustellen, gilt spätestens ab
 * 1.1.2028; ab 1.1.2027 schon, wenn der Gesamtumsatz des Vorjahres über 800.000 € lag (§ 27 Abs. 38 UStG). Bei einer
 * Gutschrift zählt der Gesamtumsatz des Gutschriftausstellers, also der Agentur, nicht der des Creators (BMF-Schreiben vom
 * 15.10.2024, Rn. 64; vom Schreiben vom 15.10.2025 nicht geändert). Die Agentur kennt ihren Umsatz, das Produkt nicht: die
 * Vorgabe ist deshalb der späteste Termin, und die Agentur trägt bei Bedarf den 1.1.2027 oder einen früheren Tag ein. Heute
 * schon möglich: eine E-Rechnung ist immer zulässig; das PDF bleibt in der Übergangszeit zulässig, weil der Creator ihm mit dem
 * Gutschriftverfahren zustimmt (BMF-Schreiben vom 15.10.2024, Rn. 63: Zustimmung formlos, auch konkludent).
 */
export const SELF_BILLING_E_INVOICE_DEFAULT_FROM = "2028-01-01";

export type SelfBillingEInvoiceReason =
  /** am Creator ausdrücklich gewählt */
  | "creator_on" | "creator_off"
  /** Vorgabe: Creator und Agentur in Deutschland, Creator regelbesteuert, Stichtag erreicht */
  | "auto_on"
  /** Vorgabe greift erst ab dem Stichtag */
  | "before_cutoff"
  /** Kleinunternehmer (§ 19 UStG; § 34a Satz 4 UStDV): immer sonstige Rechnung möglich, nur PDF */
  | "small_business"
  /** Creator nicht in Deutschland ansässig (z. B. AT, CH): keine deutsche E-Rechnungspflicht, nur PDF */
  | "creator_abroad"
  /** Agentur nicht in Deutschland ansässig: keine inländische Pflicht, nur PDF */
  | "agency_abroad";

export interface SelfBillingEInvoiceFacts {
  /** talents.self_billing_e_invoice: null = Vorgabe, true = immer mit E-Rechnung, false = nur PDF */
  creatorChoice?: boolean | null;
  /** org_settings.self_billing_e_invoice_from (Stichtag der Agentur); leer = SELF_BILLING_E_INVOICE_DEFAULT_FROM */
  agencyFrom?: unknown;
  /** Land des Creators bzw. der Agentur, wie auf der Gutschrift eingefroren */
  creatorCountry?: string | null;
  agencyCountry?: string | null;
  /** Steuerfall der Gutschrift (tax_mode) und Kleinunternehmer-Kennzeichen des Creators im Schnappschuss */
  taxMode?: string | null;
  smallBusiness?: boolean | null;
  /** Gutschriftdatum und Ende des Leistungszeitraums */
  issueDate?: unknown;
  serviceEnd?: unknown;
}

/**
 * Geht diese Gutschrift an den Creator zusätzlich als E-Rechnung? Grundsatz „Vorgabe automatisch, überschreibbar“: die Wahl
 * am Creator (wie brands.e_invoice bei Marken) schlägt die Vorgabe. Vorgabe: Creator und Agentur in Deutschland, Creator
 * regelbesteuert (kein Kleinunternehmer) und Stichtag erreicht. Maßgeblich ist der spätere Tag von Gutschriftdatum und
 * Leistungsende, denn die Übergangsregel erlaubt die sonstige Rechnung nur für bis dahin ausgeführte Umsätze und nur bis zum
 * Ablauf des Übergangsjahres (§ 27 Abs. 38 UStG; BMF-Schreiben vom 15.10.2024, Rn. 63 f.). Gleiche Regel im Portal und im Versand.
 */
export function selfBillingEInvoiceDecision(f: SelfBillingEInvoiceFacts): { wanted: boolean; reason: SelfBillingEInvoiceReason; from: string } {
  const from = iso(f.agencyFrom) ?? SELF_BILLING_E_INVOICE_DEFAULT_FROM;
  if (f.creatorChoice === true) return { wanted: true, reason: "creator_on", from };
  if (f.creatorChoice === false) return { wanted: false, reason: "creator_off", from };
  const land = (v: unknown) => (str(v) ?? "").toUpperCase();
  if (land(f.creatorCountry) !== "DE") return { wanted: false, reason: "creator_abroad", from };
  if (land(f.agencyCountry) !== "DE") return { wanted: false, reason: "agency_abroad", from };
  if (f.smallBusiness === true || taxModeFromDb(f.taxMode) === "SMALL_BUSINESS") return { wanted: false, reason: "small_business", from };
  const ref = [iso(f.issueDate), iso(f.serviceEnd)].filter((d): d is string => !!d).sort().pop() ?? null;
  if (!ref || ref < from) return { wanted: false, reason: "before_cutoff", from };
  return { wanted: true, reason: "auto_on", from };
}

// ── 0345 (Entscheidung 16.10.2026, Punkt 4): Vorschlag „E-Rechnung ab 1.1.2027“ aus dem Umsatz der Agentur ───────────────────

/** Grenze des § 27 Abs. 38 UStG: Gesamtumsatz des Vorjahres (2026) über 800.000 € → Pflicht schon ab 1.1.2027 */
export const SELF_BILLING_E_INVOICE_THRESHOLD_CENTS = 80_000_000;
/** Früher Stichtag für Agenturen über der Grenze */
export const SELF_BILLING_E_INVOICE_EARLY_FROM = "2027-01-01";
/** Jahr, dessen Umsatz über den frühen Stichtag entscheidet */
export const SELF_BILLING_E_INVOICE_BASE_YEAR = 2026;

export interface SelfBillingEInvoiceSuggestion {
  /** Jahr des gezählten Umsatzes (2026) */
  year: number;
  /** Umsatz netto mit Leistungsort im Inland aus den festgeschriebenen Rechnungen der Agentur in CreatorOffice */
  turnoverCents: Cents;
  /** nicht gezählt: Leistungsort im Ausland (Reverse Charge EU, Drittland), netto */
  abroadCents: Cents;
  /** Rechnungen in anderer Währung ohne eingefrorenen Kurs (zählen nicht mit) */
  unconverted: number;
  /** über der Grenze von 800.000 € */
  over: boolean;
  /** das Jahr ist abgeschlossen (heute nach dem 31.12.) */
  complete: boolean;
  /** Vorschlag für „E-Rechnung ab“ (1.1.2027), wenn über der Grenze und nicht schon ein Tag bis dahin eingetragen ist */
  suggestFrom: string | null;
  /** eingetragener Tag „E-Rechnung ab“ (K-450: der Hinweis nennt ihn) */
  currentFrom?: string | null;
}

/**
 * Vorschlag für den Stichtag der E-Rechnung zur Gutschrift (org_settings.self_billing_e_invoice_from). Bei Gutschriften zählt
 * der Gesamtumsatz des Gutschriftausstellers, also der Agentur (BMF-Schreiben vom 15.10.2024, Rn. 64); liegt er 2026 über
 * 800.000 €, gilt die Pflicht für sie schon ab 1.1.2027 (§ 27 Abs. 38 UStG). Gezählt wird nach dem Hausmuster der Umsatzsummen
 * (ownInvoiceRevenueAll, Praxistest K-254/K-315): Nettobeträge der festgeschriebenen Rechnungen der Agentur an Marken und Creator
 * nach Rechnungsdatum, ohne Stornierte und Stornorechnungen, ohne Verzugskosten-Belege; fremde Währung mit dem Versandkurs.
 * Wie bei der Kleinunternehmergrenze nur der Umsatz mit Leistungsort im Inland (Gesamtumsatz im Sinne des § 19 Abs. 3 UStG).
 * Umsätze außerhalb von CreatorOffice kennt das Portal nicht; der Vorschlag setzt deshalb nichts, die Agentur übernimmt ihn.
 * Nur für Agenturen in Deutschland und nur bis zum 1.1.2028 (danach gilt die Pflicht ohnehin für alle); sonst null.
 */
export function selfBillingEInvoiceSuggestion(
  rows: readonly OwnInvoiceRow[],
  opts: { today: string; agencyCountry: string | null | undefined; currentFrom?: string | null },
): SelfBillingEInvoiceSuggestion | null {
  if ((str(opts.agencyCountry) ?? "").toUpperCase() !== "DE") return null;
  const today = iso(opts.today) ?? "";
  if (today >= SELF_BILLING_E_INVOICE_DEFAULT_FROM) return null;
  const year = SELF_BILLING_E_INVOICE_BASE_YEAR;
  const { unconverted, ...rev } = ownInvoiceRevenueAll(rows, year, "EUR");
  const over = rev.domesticNetCents > SELF_BILLING_E_INVOICE_THRESHOLD_CENTS;
  const current = iso(opts.currentFrom);
  const alreadyEarly = !!current && current <= SELF_BILLING_E_INVOICE_EARLY_FROM;
  return {
    year, turnoverCents: rev.domesticNetCents, abroadCents: rev.netCents - rev.domesticNetCents, unconverted, over,
    complete: today > `${year}-12-31`, suggestFrom: over && !alreadyEarly ? SELF_BILLING_E_INVOICE_EARLY_FROM : null, currentFrom: current,
  };
}
